import type { TFunction } from 'i18next';
import { getMapImportFieldLabel, tryGetMapImportFieldLabel } from 'shared/utils/map-import-field-label';
import { EDIT_DRAWER_TOOLTIP_KEY } from '../transactions-tab/constants';
import type {
  InterpretationRuleField,
  InterpretationRuleFieldOptions,
  RuleConditionOperator,
} from './types';

const EXTRA_FIELD_DESCRIPTION_KEYS: Partial<
  Record<InterpretationRuleField, string>
> = {
  transactionNumber: 'transactionNumber',
  itemId: 'itemId',
  tax: 'tax',
  rawText: 'rawText',
  fileName: 'fileName',
  gross: 'gross',
  net: 'net',
  currency: 'currency',
  transactionDate: 'transactionDate',
  dueDate: 'dueDate',
  reference: 'reference',
  description: 'description',
  sourceType: 'sourceType',
  accountCode: 'accountCode',
  taxName: 'taxName',
  contactName: 'contactName',
  country: 'country',
  trackingOption1Id: 'trackingOption1Id',
  trackingOption2Id: 'trackingOption2Id',
};

const INTERPRETATION_RULE_FIELD_GROUPS: {
  groupKey: string;
  fields: InterpretationRuleField[];
}[] = [
  {
    groupKey: 'accounting',
    fields: ['accountCode', 'taxName'],
  },
  {
    groupKey: 'contact-document',
    fields: [
      'contactName',
      'country',
      'sourceType',
      'description',
      'itemId',
      'reference',
      'transactionNumber',
    ],
  },
  {
    groupKey: 'amounts',
    fields: ['net', 'tax', 'gross', 'currency'],
  },
  {
    groupKey: 'dates',
    fields: ['transactionDate', 'dueDate'],
  },
  {
    groupKey: 'scan-source',
    fields: ['fileName', 'rawText'],
  },
];

const TRACKING_FIELDS: InterpretationRuleField[] = [
  'trackingOption1Id',
  'trackingOption2Id',
];

export interface InterpretationRuleFieldSelectOption {
  value: InterpretationRuleField;
  label: string;
  description: string;
  searchText: string;
}

export interface InterpretationRuleFieldGroupOption {
  label: string;
  options: InterpretationRuleFieldSelectOption[];
}

function getFieldDescription(
  field: InterpretationRuleField,
  t: TFunction
): string {
  const extraKey = EXTRA_FIELD_DESCRIPTION_KEYS[field];
  if (extraKey) {
    const extractionKey = `organisation.extraction-rules.field-descriptions.${extraKey}`;
    const extractionDescription = t(extractionKey);
    if (extractionDescription !== extractionKey) {
      return extractionDescription;
    }
  }

  const tooltipKey = EDIT_DRAWER_TOOLTIP_KEY[field];
  if (tooltipKey) {
    return t(`mapImportFields.tooltips.${tooltipKey}`);
  }

  return '';
}

function resolveExtractionRuleFieldLabel(
  t: TFunction,
  field: InterpretationRuleField
): string {
  const extractionKey = `organisation.extraction-rules.fields.${field}`;
  const extractionLabel = t(extractionKey);
  if (extractionLabel !== extractionKey) {
    return extractionLabel;
  }

  const tooltipKey = EDIT_DRAWER_TOOLTIP_KEY[field];
  if (tooltipKey) {
    const mapLabel = tryGetMapImportFieldLabel(tooltipKey, t);
    if (mapLabel) {
      return mapLabel;
    }
  }

  return extractionLabel;
}

export function getInterpretationRuleFieldLabel(
  t: TFunction,
  field: InterpretationRuleField,
  fieldOptions?: Pick<InterpretationRuleFieldOptions, 'trackingFieldLabels'>
): string {
  if (field === 'trackingOption1Id' || field === 'trackingOption2Id') {
    return (
      fieldOptions?.trackingFieldLabels?.[field] ??
      resolveExtractionRuleFieldLabel(t, field)
    );
  }
  return resolveExtractionRuleFieldLabel(t, field);
}

function buildFieldSelectOption(
  field: InterpretationRuleField,
  t: TFunction,
  fieldOptions?: Pick<
    InterpretationRuleFieldOptions,
    'trackingFieldLabels' | 'showTrackingCategoryFields'
  >
): InterpretationRuleFieldSelectOption {
  const label = getInterpretationRuleFieldLabel(t, field, fieldOptions);
  const description = getFieldDescription(field, t);
  return {
    value: field,
    label,
    description,
    searchText: `${label} ${description} ${field}`.toLowerCase(),
  };
}

export function getInterpretationRuleFieldSelectOptions(
  t: TFunction,
  fieldOptions?: Pick<
    InterpretationRuleFieldOptions,
    'showTrackingCategoryFields' | 'trackingFieldLabels'
  >,
  isAction?: boolean // 1. Added isAction parameter
): InterpretationRuleFieldGroupOption[] {
  const groups = INTERPRETATION_RULE_FIELD_GROUPS.filter(
    (group) => !(isAction && group.groupKey === 'scan-source')
  )
    .map((group) => {
      let fields = [...group.fields];
      if (
        group.groupKey === 'accounting' &&
        fieldOptions?.showTrackingCategoryFields
      ) {
        fields = [...fields, ...TRACKING_FIELDS];
      }

      return {
        label: t(
          `organisation.extraction-rules.field-groups.${group.groupKey}`
        ),
        options: fields.map((field) =>
          buildFieldSelectOption(field, t, fieldOptions)
        ),
      };
    })
    .filter((group) => group.options.length > 0);

  return groups;
}

/** Flat list of fields in display order (for validation, aliases, etc.). */
export const INTERPRETATION_RULE_FIELDS: InterpretationRuleField[] =
  INTERPRETATION_RULE_FIELD_GROUPS.flatMap((group) => group.fields);

export function getInterpretationRuleFieldOptions(
  t: TFunction,
  fieldOptions?: Pick<
    InterpretationRuleFieldOptions,
    'showTrackingCategoryFields' | 'trackingFieldLabels'
  >
): { label: string; value: InterpretationRuleField }[] {
  return getInterpretationRuleFieldSelectOptions(t, fieldOptions).flatMap(
    (group) =>
      group.options.map((option) => ({
        value: option.value,
        label: option.label,
      }))
  );
}

const OPERATOR_KEYS: RuleConditionOperator[] = [
  'INCLUDES',
  'IS',
  'IS_NOT',
  'STARTS_WITH',
  'ENDS_WITH',
  'IS_EMPTY',
  'IS_NOT_EMPTY',
  'EQUALS',
  'GREATER_THAN',
  'LESS_THAN',
];

type FieldOperatorKind = 'numeric' | 'date' | 'text' | 'enum';

const FIELD_OPERATOR_KIND: Record<InterpretationRuleField, FieldOperatorKind> = {
  net: 'numeric',
  tax: 'numeric',
  gross: 'numeric',
  transactionDate: 'date',
  dueDate: 'date',
  currency: 'enum',
  sourceType: 'enum',
  country: 'enum',
  accountCode: 'text',
  taxName: 'text',
  contactName: 'text',
  description: 'text',
  reference: 'text',
  transactionNumber: 'text',
  itemId: 'text',
  trackingOption1Id: 'text',
  trackingOption2Id: 'text',
  rawText: 'text',
  fileName: 'text',
};

const OPERATORS_BY_KIND: Record<FieldOperatorKind, RuleConditionOperator[]> = {
  numeric: [
    'EQUALS',
    'IS',
    'IS_NOT',
    'GREATER_THAN',
    'LESS_THAN',
    'IS_EMPTY',
    'IS_NOT_EMPTY',
  ],
  date: [
    'IS',
    'IS_NOT',
    'EQUALS',
    'GREATER_THAN',
    'LESS_THAN',
    'IS_EMPTY',
    'IS_NOT_EMPTY',
  ],
  text: [
    'INCLUDES',
    'IS',
    'IS_NOT',
    'STARTS_WITH',
    'ENDS_WITH',
    'EQUALS',
    'IS_EMPTY',
    'IS_NOT_EMPTY',
  ],
  enum: ['IS', 'IS_NOT', 'EQUALS', 'INCLUDES', 'IS_EMPTY', 'IS_NOT_EMPTY'],
};

export function getOperatorOptionsForField(
  field: InterpretationRuleField | null | undefined,
  t: TFunction
): { label: string; value: RuleConditionOperator }[] {
  if (!field) return [];
  const kind = FIELD_OPERATOR_KIND[field];
  return OPERATORS_BY_KIND[kind].map((value) => ({
    value,
    label: getOperatorLabel(t, value),
  }));
}

export function isOperatorValidForField(
  field: InterpretationRuleField | null | undefined,
  operator: RuleConditionOperator | null | undefined
): boolean {
  if (!field || !operator) return false;
  return OPERATORS_BY_KIND[FIELD_OPERATOR_KIND[field]].includes(operator);
}

export function getFieldValueOptions(
  field: InterpretationRuleField | null | undefined,
  fieldOptions?: InterpretationRuleFieldOptions
): { label: string; value: string }[] | undefined {
  if (!field || !fieldOptions) return undefined;
  if (field === 'accountCode') return fieldOptions.accountOptions;
  if (field === 'taxName') return fieldOptions.taxOptions;
  if (field === 'sourceType') return fieldOptions.sourceTypeOptions;
  if (field === 'contactName') return fieldOptions.contactNameOptions;
  if (field === 'country') return fieldOptions.countryOptions;
  if (field === 'itemId') return fieldOptions.itemOptions;
  if (field === 'trackingOption1Id') return fieldOptions.trackingOption1Options;
  if (field === 'trackingOption2Id') return fieldOptions.trackingOption2Options;
  return undefined;
}

export function getOperatorLabel(
  t: TFunction,
  operator: RuleConditionOperator
): string {
  return t(`organisation.extraction-rules.operators.${operator}`, {
    defaultValue: OPERATOR_LABELS[operator],
  });
}

export function getOperatorOptions(t: TFunction): {
  label: string;
  value: RuleConditionOperator;
}[] {
  return OPERATOR_KEYS.map((value) => ({
    value,
    label: getOperatorLabel(t, value),
  }));
}

export const OPERATOR_LABELS: Record<RuleConditionOperator, string> = {
  INCLUDES: 'includes',
  IS: 'is',
  IS_NOT: 'is not',
  STARTS_WITH: 'starts with',
  ENDS_WITH: 'ends with',
  IS_EMPTY: 'is empty',
  IS_NOT_EMPTY: 'is not empty',
  EQUALS: 'equals',
  GREATER_THAN: 'greater than',
  LESS_THAN: 'less than',
};

/** @deprecated Use getOperatorOptions(t) */
export const OPERATOR_OPTIONS: {
  label: string;
  value: RuleConditionOperator;
}[] = OPERATOR_KEYS.map((value) => ({
  value,
  label: OPERATOR_LABELS[value],
}));

export function operatorNeedsValue(operator: RuleConditionOperator): boolean {
  return operator !== 'IS_EMPTY' && operator !== 'IS_NOT_EMPTY';
}

/** Text-matching operators accept free-form values, not only list options. */
export function operatorAllowsFreeTextValue(
  operator: RuleConditionOperator | null | undefined
): boolean {
  return (
    operator === 'INCLUDES' ||
    operator === 'STARTS_WITH' ||
    operator === 'ENDS_WITH'
  );
}

export const FIELD_ALIASES: Record<string, InterpretationRuleField> = {
  contact: 'contactName',
  'contact name': 'contactName',
  supplier: 'contactName',
  'supplier name': 'contactName',
  vendor: 'contactName',
  'vendor name': 'contactName',
  customer: 'contactName',
  'customer name': 'contactName',
  client: 'contactName',
  'client name': 'contactName',
  company: 'contactName',
  'company name': 'contactName',
  name: 'contactName',
  merchant: 'contactName',
  'merchant name': 'contactName',
  payee: 'contactName',
  payer: 'contactName',
  country: 'country',
  'country code': 'country',
  'country name': 'country',
  nation: 'country',
  account: 'accountCode',
  'account name': 'accountCode',
  'account code': 'accountCode',
  category: 'accountCode',
  'expense category': 'accountCode',
  'gl account': 'accountCode',
  'ledger account': 'accountCode',
  'nominal code': 'accountCode',
  'nominal account': 'accountCode',
  expense: 'accountCode',
  expenses: 'accountCode',
  'expense account': 'accountCode',
  'cost centre': 'accountCode',
  'cost center': 'accountCode',
  tax: 'taxName',
  vat: 'taxName',
  gst: 'taxName',
  'tax name': 'taxName',
  'tax rate': 'taxName',
  'tax rate name': 'taxName',
  'vat rate': 'taxName',
  'tax code': 'taxName',
  'tax rate code': 'taxName',
  'vat code': 'taxName',
  description: 'description',
  'line description': 'description',
  'item description': 'description',
  memo: 'description',
  notes: 'description',
  narrative: 'description',
  details: 'description',
  item: 'itemId',
  'line item': 'itemId',
  'item name': 'itemId',
  product: 'itemId',
  'product name': 'itemId',
  service: 'itemId',
  currency: 'currency',
  'currency code': 'currency',
  date: 'transactionDate',
  'transaction date': 'transactionDate',
  'invoice date': 'transactionDate',
  'document date': 'transactionDate',
  'due date': 'dueDate',
  'payment due date': 'dueDate',
  source: 'sourceType',
  'source type': 'sourceType',
  'transaction type': 'sourceType',
  type: 'sourceType',
  bill: 'sourceType',
  invoice: 'sourceType',
  purchase: 'sourceType',
  sale: 'sourceType',
  receipt: 'sourceType',
  reference: 'reference',
  'reference number': 'reference',
  ref: 'reference',
  'ref no': 'reference',
  'invoice number': 'transactionNumber',
  'invoice no': 'transactionNumber',
  'bill number': 'transactionNumber',
  'document number': 'transactionNumber',
  net: 'net',
  'net amount': 'net',
  amount: 'net',
  value: 'net',
  gross: 'gross',
  total: 'gross',
  'total amount': 'gross',
  'tax amount': 'tax',
  filename: 'fileName',
  'file name': 'fileName',
  'uploaded file': 'fileName',
  'raw text': 'rawText',
  'scanned text': 'rawText',
  'ocr text': 'rawText',
  'tracking category 1': 'trackingOption1Id',
  'tracking category 2': 'trackingOption2Id',
  'tracking option 1': 'trackingOption1Id',
  'tracking option 2': 'trackingOption2Id',
  'first tracking category': 'trackingOption1Id',
  'second tracking category': 'trackingOption2Id',
  'tracking 1': 'trackingOption1Id',
  'tracking 2': 'trackingOption2Id',
};

/** Longest aliases first so e.g. "tax rate name" wins over "tax". */
export const FIELD_ALIAS_ENTRIES = Object.entries(FIELD_ALIASES).sort(
  (a, b) => b[0].length - a[0].length
);
